import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { 
  Check, ArrowRight, Clock, Code, Users, Shield, Truck, MapPin, 
  CreditCard, FileText, Mail, Phone, ChevronDown, Menu, X, Package,
  Zap, Globe, Star, Calendar, CheckCircle2, TrendingUp, Award,
  Smartphone, Server, Database, Lock, MessageSquare, BarChart3,
  Target, Building, Car, Bell, Settings, Eye, Play, Quote,
  HelpCircle, ChevronRight, ExternalLink, Briefcase, Layers,
  Bot, Timer, Infinity, RefreshCw, Scale, Rocket
} from 'lucide-react';

// ════════════════════════════════════════════════════════════════════════════════
// AUTONOMOUS AS - VARETAXI PROPOSAL
// Customer-focused, world-class proposal document
// ════════════════════════════════════════════════════════════════════════════════

const MARTIN_IMAGE = "https://customer-assets.emergentagent.com/job_win95-retro/artifacts/1txzsvha_603a4205317945628ebfbb5a_Martin.jpg";
const ERIK_IMAGE = "https://customer-assets.emergentagent.com/job_win95-retro/artifacts/ri2p47ex_image.png";

// Design tokens - pure black
const c = {
  bg: '#000000',
  bgAlt: '#050505',
  bgCard: '#0a0a0a',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255,255,255,0.7)',
  textSubtle: 'rgba(255,255,255,0.4)',
  accent: '#ffffff',
};

const ease = [0.22, 1, 0.36, 1];

// ═══════════════════════════════════════════════════════════════════════════
// CORE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease }}
    >
      {children}
    </motion.div>
  );
}

function Section({ children, id, alt = false }) {
  return (
    <section id={id} style={{
      padding: 'clamp(60px, 10vh, 120px) clamp(20px, 5vw, 80px)',
      background: alt ? c.bgAlt : c.bg,
      borderBottom: `1px solid ${c.border}`,
    }}>
      <div style={{ maxWidth: '880px', margin: '0 auto' }}>{children}</div>
    </section>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: '10px', fontWeight: '600', letterSpacing: '0.2em',
      textTransform: 'uppercase', color: c.textSubtle, marginBottom: '12px',
    }}>{children}</div>
  );
}

function Title({ children, size = 'lg' }) {
  const sizes = { xl: 'clamp(40px, 8vw, 64px)', lg: 'clamp(28px, 4vw, 40px)', md: 'clamp(22px, 3vw, 28px)', sm: '18px' };
  return (
    <h2 style={{
      fontSize: sizes[size], fontWeight: '300', letterSpacing: '-0.03em',
      lineHeight: 1.15, color: c.text, marginBottom: '16px',
    }}>{children}</h2>
  );
}

function Text({ children, style = {} }) {
  return (
    <p style={{
      fontSize: '15px', lineHeight: 1.7, color: c.textMuted, marginBottom: '14px', ...style,
    }}>{children}</p>
  );
}

function Card({ children, highlight = false, style = {} }) {
  return (
    <div style={{
      background: c.bgCard, border: `1px solid ${highlight ? c.borderLight : c.border}`,
      borderRadius: '12px', padding: 'clamp(20px, 3vw, 32px)', ...style,
    }}>{children}</div>
  );
}

function CheckItem({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
      <Check size={14} color={c.textMuted} style={{ marginTop: '3px', flexShrink: 0 }} />
      <span style={{ fontSize: '14px', color: c.textMuted, lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

function Stat({ value, label, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: '200', color: c.text, marginBottom: '6px', letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '2px' }}>{label}</div>
      {sub && <div style={{ fontSize: '11px', color: c.textSubtle }}>{sub}</div>}
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', background: c.border, margin: '28px 0' }} />;
}

// ═══════════════════════════════════════════════════════════════════════════
// NAVIGATION
// ═══════════════════════════════════════════════════════════════════════════

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };
  
  const items = [
    { id: 'about', label: 'Om oss' },
    { id: 'solution', label: 'Løsningen' },
    { id: 'phases', label: 'Leveranse' },
    { id: 'investment', label: 'Pris' },
    { id: 'contact', label: 'Kontakt' },
  ];
  
  return (
    <>
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: scrolled ? 0 : -80 }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <div style={{
          maxWidth: '940px', margin: '0 auto',
          padding: '14px clamp(20px, 5vw, 40px)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <a href="/" style={{ 
            fontSize: '22px', 
            fontWeight: '100', 
            color: c.text, 
            textDecoration: 'none',
            letterSpacing: '-0.02em',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          }}>a.</a>
          <div className="nav-desktop" style={{ display: 'flex', gap: '28px' }}>
            {items.map((item) => (
              <button key={item.id} onClick={() => scrollTo(item.id)} style={{
                background: 'none', border: 'none', color: c.textMuted,
                fontSize: '13px', cursor: 'pointer', padding: 0,
                transition: 'color 0.2s',
              }}>{item.label}</button>
            ))}
          </div>
          <button className="nav-mobile" onClick={() => setMenuOpen(!menuOpen)} style={{
            display: 'none', background: 'none', border: 'none',
            color: c.text, padding: '8px', cursor: 'pointer',
          }}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </motion.nav>
      
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.98)', zIndex: 999, paddingTop: '70px', paddingLeft: '20px' }}>
            {items.map((item) => (
              <button key={item.id} onClick={() => scrollTo(item.id)} style={{
                display: 'block', background: 'none', border: 'none',
                color: c.text, fontSize: '18px', padding: '14px 0', cursor: 'pointer',
              }}>{item.label}</button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      
      <style>{`
        @media (max-width: 768px) {
          .nav-desktop { display: none !important; }
          .nav-mobile { display: block !important; }
        }
      `}</style>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HERO - With app mockup on right side
// ═══════════════════════════════════════════════════════════════════════════

function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  
  return (
    <section style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: 'clamp(60px, 12vh, 120px) clamp(20px, 5vw, 80px)', background: c.bg,
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%' }}>
        {/* Main hero content with mockup */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr auto', 
          gap: 'clamp(40px, 6vw, 80px)',
          alignItems: 'center',
        }} className="hero-grid">
          {/* Left side - Text content */}
          <div>
            <FadeIn>
              <div style={{ fontSize: '11px', color: c.textSubtle, letterSpacing: '0.15em', marginBottom: '32px' }}>
                TILBUD • AUTONOMOUS AS • JANUAR 2025
              </div>
            </FadeIn>
            
            <FadeIn delay={0.05}>
              <h1 style={{
                fontSize: 'clamp(44px, 10vw, 80px)', fontWeight: '200',
                letterSpacing: '-0.04em', lineHeight: 0.95, color: c.text, marginBottom: '16px',
              }}>VareTaxi</h1>
            </FadeIn>
            
            <FadeIn delay={0.1}>
              <div style={{
                display: 'inline-block', fontSize: '10px', color: c.textSubtle,
                letterSpacing: '0.1em', padding: '6px 14px',
                border: `1px solid ${c.border}`, borderRadius: '100px', marginBottom: '28px',
              }}>MIDLERTIDIG ARBEIDSNAVN</div>
            </FadeIn>
            
            <FadeIn delay={0.15}>
              <p style={{
                fontSize: 'clamp(17px, 2.2vw, 22px)', fontWeight: '300',
                color: c.textMuted, maxWidth: '520px', lineHeight: 1.5, marginBottom: '36px',
              }}>
                En digital markedsplass som kobler de som trenger varetransport med sjåfører som har ledig kapasitet.
              </p>
            </FadeIn>
            
            <FadeIn delay={0.2}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '48px' }}>
                <button onClick={() => scrollTo('contact')} style={{
                  background: c.text, color: c.bg, border: 'none', borderRadius: '8px',
                  padding: '14px 28px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                }}>Ta kontakt</button>
                <button onClick={() => scrollTo('greeting')} style={{
                  background: 'transparent', color: c.text, border: `1px solid ${c.border}`,
                  borderRadius: '8px', padding: '14px 28px', fontSize: '13px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>Se tilbudet <ChevronDown size={14} /></button>
              </div>
            </FadeIn>
          </div>
          
          {/* Right side - Video Mockup with phone frame */}
          <FadeIn delay={0.3}>
            <div className="hero-mockup" style={{
              position: 'relative',
              width: 'clamp(200px, 26vw, 280px)',
            }}>
              {/* Floating effect shadow */}
              <div style={{
                position: 'absolute',
                bottom: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '70%',
                height: '30px',
                background: 'radial-gradient(ellipse, rgba(0,0,0,0.4) 0%, transparent 70%)',
                filter: 'blur(8px)',
              }} />
              
              {/* Phone device */}
              <div style={{
                background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
                borderRadius: '36px',
                padding: '6px',
                boxShadow: `
                  0 0 0 1px rgba(255,255,255,0.1),
                  0 25px 50px -12px rgba(0,0,0,0.5),
                  inset 0 1px 0 rgba(255,255,255,0.05)
                `,
                position: 'relative',
              }}>
                {/* Dynamic Island */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '90px',
                  height: '24px',
                  background: '#000',
                  borderRadius: '16px',
                  zIndex: 10,
                }} />
                
                {/* Screen with video */}
                <div style={{
                  borderRadius: '30px',
                  overflow: 'hidden',
                  background: '#000',
                }}>
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  >
                    <source src="/hero_video.webm" type="video/webm" />
                    <source src="/hero_video.mp4" type="video/mp4" />
                  </video>
                </div>
                
                {/* Home indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '90px',
                  height: '4px',
                  background: 'rgba(255,255,255,0.3)',
                  borderRadius: '2px',
                }} />
              </div>
              
              {/* Subtle glow effect */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '150%',
                height: '150%',
                background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 60%)',
                pointerEvents: 'none',
                zIndex: -1,
              }} />
            </div>
          </FadeIn>
        </div>
        
        {/* Stats row */}
        <FadeIn delay={0.35}>
          <div style={{
            paddingTop: '48px', marginTop: '32px', borderTop: `1px solid ${c.border}`,
            display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px',
          }} className="hero-stats">
            {[
              { label: 'PILOT', value: '2-4 uker', sub: '300.000 kr' },
              { label: 'GTM', value: '4-8 uker', sub: '200.000 kr' },
              { label: 'PRODUKSJON', value: 'Etter GTM', sub: '950.000 kr' },
              { label: 'PLATTFORM', value: 'Web + App', sub: 'iOS & Android' },
              { label: 'EIERSKAP', value: '100%', sub: 'Din kode' },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ fontSize: '10px', color: c.textSubtle, letterSpacing: '0.12em', marginBottom: '6px' }}>{item.label}</div>
                <div style={{ fontSize: '15px', color: c.text, marginBottom: '2px' }}>{item.value}</div>
                <div style={{ fontSize: '11px', color: c.textSubtle }}>{item.sub}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
      
      <style>{`
        @media (max-width: 900px) {
          .hero-grid { 
            grid-template-columns: 1fr !important; 
            text-align: center;
          }
          .hero-mockup { 
            margin: 32px auto 0 !important;
            width: clamp(200px, 50vw, 280px) !important;
          }
        }
        @media (max-width: 768px) {
          .hero-stats { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 500px) {
          .hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PERSONAL GREETING TO LØVE
// ═══════════════════════════════════════════════════════════════════════════

function PersonalGreeting() {
  return (
    <Section id="greeting">
      <FadeIn>
        <div style={{
          maxWidth: '720px',
          margin: '0 auto',
        }}>
          {/* Letter content - clean design */}
          <div style={{
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: '16px',
            padding: 'clamp(28px, 5vw, 48px)',
          }}>
            <Text style={{ fontSize: '17px', lineHeight: 1.8, marginBottom: '24px' }}>
              Hei Løve,
            </Text>
            
            <Text style={{ lineHeight: 1.85, marginBottom: '24px' }}>
              Tusen takk for et hyggelig og inspirerende møte! Det var givende å høre om visjonen din 
              – en idé som virkelig treffer et reelt behov i markedet.
            </Text>
            
            <Text style={{ lineHeight: 1.85, marginBottom: '24px' }}>
              Vi opplever at behovet dere beskriver er reelt, og at tidspunktet for å teste 
              konseptet er godt. Med riktig teknologi og tilnærming tror vi dette kan bli en 
              sterk posisjon i det norske markedet.
            </Text>
            
            <Text style={{ lineHeight: 1.85, marginBottom: '24px' }}>
              Det som gjør oss spesielt engasjerte er muligheten til å være med på hele reisen. Vi er 
              ikke bare utviklere – vi er <strong style={{ color: c.text }}>forretningsfolk</strong> som brenner for å hjelpe gründere 
              med å realisere sine visjoner. Fra første kodelinje til første kunde, fra juridisk struktur 
              til markedsføringsstrategi.
            </Text>
            
            <Text style={{ lineHeight: 1.85, marginBottom: '32px' }}>
              Dette tilbudet er vårt forslag til hvordan vi sammen kan ta ideen fra konsept til marked – 
              raskt, trygt og med full kontroll.
            </Text>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '14px',
              paddingTop: '24px',
              borderTop: `1px solid ${c.border}`,
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: `2px solid ${c.border}`,
              }}>
                <img 
                  src={MARTIN_IMAGE} 
                  alt="Martin" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div>
                <div style={{ fontSize: '15px', color: c.text, fontWeight: '500' }}>Martin Kviteberg</div>
                <div style={{ fontSize: '13px', color: c.textSubtle }}>Daglig leder, Autonomous AS</div>
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ABOUT AUTONOMOUS - BUILD TRUST (UPDATED)
// ═══════════════════════════════════════════════════════════════════════════

function AboutSection() {
  return (
    <Section id="about" alt>
      <FadeIn>
        <Label>Hvem vi er</Label>
        <Title>Hvorfor Autonomous?</Title>
        <Text style={{ maxWidth: '650px', marginBottom: '40px' }}>
          Vi bygger software annerledes. Med agent-basert AI-utvikling jobber vi i sprinter på <strong style={{ color: c.text }}>minutter, ikke uker</strong>. 
          Mennesker validerer kode, sikkerhet og kvalitet – AI gjør grovjobben.
        </Text>
      </FadeIn>
      
      {/* Core differentiators - 3 cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }} className="about-cards">
        {[
          { 
            icon: Bot, 
            title: 'Agent-basert utvikling', 
            desc: 'AI-agenter bygger kode i sanntid. Vi trenger ikke et stort utviklingsteam – bare de rette menneskene til å styre og validere.' 
          },
          { 
            icon: Timer, 
            title: 'Sprinter på minutter', 
            desc: 'Mens andre planlegger 2-ukers sprinter, itererer vi flere ganger per dag. Raskere feedback, raskere resultater.' 
          },
          { 
            icon: Infinity, 
            title: 'Fleksibel utvikling', 
            desc: 'Endringer og justeringer innenfor avtalt fase og mål er inkludert. Nye funksjonsområder vurderes og prioriteres sammen.' 
          },
        ].map((item, i) => (
          <FadeIn key={i} delay={0.05 * i}>
            <Card>
              <item.icon size={20} color={c.textMuted} style={{ marginBottom: '12px' }} />
              <div style={{ fontSize: '15px', fontWeight: '500', color: c.text, marginBottom: '8px' }}>{item.title}</div>
              <Text style={{ marginBottom: 0, fontSize: '13px' }}>{item.desc}</Text>
            </Card>
          </FadeIn>
        ))}
      </div>
      
      {/* What this means for you */}
      <FadeIn delay={0.3}>
        <Card style={{ background: c.bgAlt, marginBottom: '48px' }}>
          <div style={{ fontSize: '14px', color: c.text, marginBottom: '12px' }}>Hva betyr dette for deg?</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {[
              { label: 'Raskere leveranse', desc: 'Pilot på 2-4 uker, ikke måneder' },
              { label: 'Lavere risiko', desc: 'Små investeringer, raske kursendringer' },
              { label: 'Fleksibilitet', desc: 'Justeringer innenfor avtalt scope inkludert' },
              { label: 'Full kontroll', desc: '100% eierskap til all kode og data' },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ fontSize: '13px', color: c.text, marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '12px', color: c.textSubtle }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </Card>
      </FadeIn>
      
      {/* Full journey partner */}
      <FadeIn delay={0.35}>
        <div style={{ marginBottom: '48px' }}>
          <Label>Mer enn utvikling</Label>
          <Title size="md">Din partner på hele forretningsreisen</Title>
          <Text style={{ maxWidth: '650px', marginBottom: '24px' }}>
            Vi er erfarne forretningsfolk som ønsker å hjelpe våre kunder med mer enn bare kode. 
            Vår ambisjon er å være en strategisk partner som bistår med <strong style={{ color: c.text }}>kommersialisering 
            av spennende konsepter</strong> – fra idé til marked.
          </Text>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(5, 1fr)', 
            gap: '12px',
          }} className="partner-cards">
            {[
              { icon: Scale, label: 'Juridisk & struktur' },
              { icon: Code, label: 'Utvikling & teknologi' },
              { icon: Rocket, label: 'Go-to-market' },
              { icon: TrendingUp, label: 'Markedsføring' },
              { icon: Users, label: 'Forretningsutvikling' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                background: c.bgCard,
                border: `1px solid ${c.border}`,
                borderRadius: '8px',
              }}>
                <item.icon size={16} color={c.textMuted} />
                <span style={{ fontSize: '13px', color: c.textMuted }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>
      
      {/* Team - Expanded */}
      <FadeIn delay={0.4}>
        <Label>Teamet</Label>
        <Title size="md">Hvem står bak Autonomous?</Title>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: '24px',
          marginTop: '24px',
        }} className="team-grid">
          {/* Martin */}
          <Card style={{ padding: '28px' }}>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div style={{
                width: '80px', height: '80px', borderRadius: '50%',
                overflow: 'hidden', border: `2px solid ${c.border}`, flexShrink: 0,
              }}>
                <img src={MARTIN_IMAGE} alt="Martin Kviteberg" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ paddingTop: '8px' }}>
                <div style={{ fontSize: '18px', color: c.text, fontWeight: '500', marginBottom: '4px' }}>Martin Kviteberg</div>
                <div style={{ fontSize: '13px', color: c.textSubtle, marginBottom: '8px' }}>Daglig leder & Gründer</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ 
                    fontSize: '10px', color: c.textSubtle, padding: '4px 8px', 
                    background: 'rgba(255,255,255,0.05)', borderRadius: '4px',
                  }}>Forretningsutvikling</span>
                  <span style={{ 
                    fontSize: '10px', color: c.textSubtle, padding: '4px 8px', 
                    background: 'rgba(255,255,255,0.05)', borderRadius: '4px',
                  }}>Produkt</span>
                </div>
              </div>
            </div>
            <Text style={{ fontSize: '13px', lineHeight: 1.7, marginBottom: 0 }}>
              10+ års erfaring som <strong style={{ color: c.text }}>Direktør for Forretningsutvikling</strong> i Adonis HR, 
              et ledende norsk selskap innen HR- og lønnssystemer for maritim industri. Seriegrunder med 
              lidenskap for å bygge selskaper fra bunn. Kombinerer dyp forretningsforståelse med 
              praktisk produktutvikling.
            </Text>
          </Card>
          
          {/* Erik */}
          <Card style={{ padding: '28px' }}>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div style={{
                width: '80px', height: '80px', borderRadius: '50%',
                overflow: 'hidden', border: `2px solid ${c.border}`, flexShrink: 0,
              }}>
                <img src={ERIK_IMAGE} alt="Erik Hoffmann-Dahl" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ paddingTop: '8px' }}>
                <div style={{ fontSize: '18px', color: c.text, fontWeight: '500', marginBottom: '4px' }}>Erik Hoffmann-Dahl</div>
                <div style={{ fontSize: '13px', color: c.textSubtle, marginBottom: '8px' }}>Partner & Legal Counsel</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ 
                    fontSize: '10px', color: c.textSubtle, padding: '4px 8px', 
                    background: 'rgba(255,255,255,0.05)', borderRadius: '4px',
                  }}>Forretningsjus</span>
                  <span style={{ 
                    fontSize: '10px', color: c.textSubtle, padding: '4px 8px', 
                    background: 'rgba(255,255,255,0.05)', borderRadius: '4px',
                  }}>M&A</span>
                </div>
              </div>
            </div>
            <Text style={{ fontSize: '13px', lineHeight: 1.7, marginBottom: 0 }}>
              <strong style={{ color: c.text }}>Partner og daglig leder i Hoffmann Thinn Advokatfirma AS</strong> i Bergen. 
              Tidligere senioradvokat hos Wikborg Rein med internasjonal erfaring fra London. 
              Spesialist på selskapsrett, transaksjoner, finansiering og kontraktsrett. Sikrer at alle 
              juridiske aspekter er ivaretatt – fra selskapsstruktur til GDPR og brukervilkår.
            </Text>
          </Card>
        </div>
        
        {/* Development Team in India */}
        <Card style={{ marginTop: '24px', padding: '24px', background: c.bgAlt }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ 
              width: '48px', height: '48px', borderRadius: '12px',
              background: 'rgba(255,255,255,0.05)', 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Code size={24} style={{ color: c.textSubtle }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', color: c.text, fontWeight: '500', marginBottom: '4px' }}>
                Utviklingsteam
              </div>
              <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '12px' }}>
                Kvalitetssikring & Teknisk Validering
              </div>
              <Text style={{ fontSize: '13px', lineHeight: 1.7, marginBottom: '16px' }}>
                Vi samarbeider med et dedikert utviklingsteam i India som spesialiserer seg på <strong style={{ color: c.text }}>kvalitetssikring av AI-generert kode</strong>. 
                Teamet skriver ikke kode selv – de <strong style={{ color: c.text }}>gjennomgår og validerer</strong> all kode som våre AI-agenter produserer.
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {['Kodegjennomgang', 'Sikkerhetstesting', 'Skalering', 'Best practices'].map((tag, i) => (
                  <span key={i} style={{ 
                    fontSize: '11px', color: c.textSubtle, padding: '5px 10px', 
                    background: 'rgba(255,255,255,0.05)', borderRadius: '6px',
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </FadeIn>
      
      <style>{`
        @media (max-width: 768px) {
          .about-cards { grid-template-columns: 1fr !important; }
          .team-grid { grid-template-columns: 1fr !important; }
          .partner-cards { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .partner-cards { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THE PROBLEM
// ═══════════════════════════════════════════════════════════════════════════

function ProblemSection() {
  return (
    <Section id="problem">
      <FadeIn>
        <Label>Problemet</Label>
        <Title>Varetransport i dag er tungvint</Title>
      </FadeIn>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '40px' }}>
        {[
          { title: 'For dyrt', desc: 'Budtjenester koster ofte 500-1500 kr for enkle oppdrag. Overkill for de fleste.' },
          { title: 'For tregt', desc: 'Posten tar dager. Budtjenester må bestilles lang tid i forveien.' },
          { title: 'For komplisert', desc: 'Ingen vet hva det koster før de ringer rundt. Prisene varierer vilt.' },
        ].map((item, i) => (
          <FadeIn key={i} delay={0.05 * i}>
            <Card>
              <div style={{ fontSize: '15px', fontWeight: '500', color: c.text, marginBottom: '8px' }}>{item.title}</div>
              <Text style={{ marginBottom: 0, fontSize: '13px' }}>{item.desc}</Text>
            </Card>
          </FadeIn>
        ))}
      </div>
      
      <FadeIn delay={0.2}>
        <Card style={{ background: c.bgAlt }}>
          <div style={{ fontSize: '14px', color: c.text, marginBottom: '12px' }}>Typiske bruksscenarier</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            {[
              { icon: Package, label: 'Pakker fra Finn.no' },
              { icon: Building, label: 'Møbler fra IKEA' },
              { icon: Car, label: 'Henting fra butikk' },
              { icon: Truck, label: 'Flytting av enkeltting' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <item.icon size={16} color={c.textSubtle} />
                <span style={{ fontSize: '13px', color: c.textMuted }}>{item.label}</span>
              </div>
            ))}
          </div>
        </Card>
      </FadeIn>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THE SOLUTION
// ═══════════════════════════════════════════════════════════════════════════

function SolutionSection() {
  return (
    <Section id="solution" alt>
      <FadeIn>
        <Label>Løsningen</Label>
        <Title>VareTaxi gjør det enkelt</Title>
        <Text style={{ maxWidth: '550px', marginBottom: '40px' }}>
          En markedsplass som kobler kunder med sjåfører. Fastpris på sekunder. 
          Levering samme dag. Full oversikt fra bestilling til levering.
        </Text>
      </FadeIn>
      
      {/* Core flow - improved design with same width as content */}
      <FadeIn delay={0.1}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '12px',
          marginBottom: '48px', 
          maxWidth: '100%',
        }} className="flow-grid">
          {[
            { num: '1', icon: MapPin, title: 'Bestill', desc: 'Angi A → B' },
            { num: '2', icon: CreditCard, title: 'Fastpris', desc: 'Se pris direkte' },
            { num: '3', icon: Bell, title: 'Match', desc: 'Sjåfør aksepterer' },
            { num: '4', icon: Truck, title: 'Levering', desc: 'Følg i sanntid' },
            { num: '5', icon: Star, title: 'Ferdig', desc: 'Betal + tips' },
          ].map((step, i) => (
            <div key={i} style={{
              background: c.bgCard, 
              border: `1px solid ${c.border}`, 
              borderRadius: '12px',
              padding: 'clamp(14px, 2vw, 20px) clamp(10px, 1.5vw, 16px)', 
              textAlign: 'center', 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{ 
                fontSize: '10px', 
                color: c.bg, 
                background: c.text,
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '10px',
                fontWeight: '500',
              }}>{step.num}</div>
              <step.icon size={20} color={c.textMuted} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', color: c.text, marginBottom: '4px', fontWeight: '500' }}>{step.title}</div>
              <div style={{ fontSize: 'clamp(10px, 1.2vw, 12px)', color: c.textSubtle }}>{step.desc}</div>
            </div>
          ))}
        </div>
      </FadeIn>
      
      {/* App Video Preview - World-class presentation */}
      <FadeIn delay={0.2}>
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            fontSize: '12px', 
            color: c.textSubtle, 
            marginBottom: '24px', 
            letterSpacing: '0.15em',
            textAlign: 'center',
          }}>
            SE APPEN I AKSJON
          </div>
          
          {/* Video showcase - cinematic style */}
          <div style={{
            position: 'relative',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)',
            borderRadius: '24px',
            padding: 'clamp(32px, 5vw, 64px)',
            border: `1px solid ${c.border}`,
            overflow: 'hidden',
          }}>
            {/* Background gradient accent */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '800px',
              height: '800px',
              background: 'radial-gradient(circle, rgba(59,130,246,0.04) 0%, transparent 50%)',
              pointerEvents: 'none',
            }} />
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr auto 1fr', 
              gap: 'clamp(24px, 4vw, 48px)',
              alignItems: 'center',
              position: 'relative',
            }} className="video-showcase-grid">
              
              {/* Left features */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {[
                  { 
                    icon: Package, 
                    title: 'Send pakke', 
                    desc: 'Velg henting og levering på sekunder. Få fastpris umiddelbart.',
                  },
                  { 
                    icon: Car, 
                    title: 'Bli sjåfør', 
                    desc: 'Registrer deg og tjen penger på din tid. Full fleksibilitet.',
                  },
                ].map((item, i) => (
                  <div key={i} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '16px',
                    textAlign: 'right',
                    flexDirection: 'row-reverse',
                  }} className="feature-item-left">
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: `1px solid ${c.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <item.icon size={20} color={c.textMuted} />
                    </div>
                    <div>
                      <div style={{ fontSize: '15px', color: c.text, marginBottom: '4px', fontWeight: '500' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '13px', color: c.textSubtle, lineHeight: 1.5 }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Center - Video in elegant phone frame */}
              <div style={{
                position: 'relative',
                width: 'clamp(200px, 28vw, 280px)',
              }}>
                {/* Floating effect shadow */}
                <div style={{
                  position: 'absolute',
                  bottom: '-20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80%',
                  height: '40px',
                  background: 'radial-gradient(ellipse, rgba(0,0,0,0.4) 0%, transparent 70%)',
                  filter: 'blur(10px)',
                }} />
                
                {/* Phone device */}
                <div style={{
                  background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
                  borderRadius: '40px',
                  padding: '8px',
                  boxShadow: `
                    0 0 0 1px rgba(255,255,255,0.1),
                    0 25px 50px -12px rgba(0,0,0,0.5),
                    inset 0 1px 0 rgba(255,255,255,0.05)
                  `,
                  position: 'relative',
                }}>
                  {/* Dynamic Island */}
                  <div style={{
                    position: 'absolute',
                    top: '14px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '28px',
                    background: '#000',
                    borderRadius: '20px',
                    zIndex: 10,
                  }} />
                  
                  {/* Screen */}
                  <div style={{
                    borderRadius: '32px',
                    overflow: 'hidden',
                    background: '#000',
                  }}>
                    <video 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                    >
                      <source src="/varetaxi_preview.webm" type="video/webm" />
                      <source src="/varetaxi_preview.mp4" type="video/mp4" />
                    </video>
                  </div>
                  
                  {/* Home indicator */}
                  <div style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '4px',
                    background: 'rgba(255,255,255,0.3)',
                    borderRadius: '2px',
                  }} />
                </div>
              </div>
              
              {/* Right features */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {[
                  { 
                    icon: MapPin, 
                    title: 'Sanntidssporing', 
                    desc: 'Følg pakken hele veien. Live oppdateringer på kartet.',
                  },
                  { 
                    icon: CreditCard, 
                    title: 'Sikker betaling', 
                    desc: 'Betal trygt i appen. Ingen kontanter, ingen stress.',
                  },
                ].map((item, i) => (
                  <div key={i} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '16px',
                  }} className="feature-item-right">
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: `1px solid ${c.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <item.icon size={20} color={c.textMuted} />
                    </div>
                    <div>
                      <div style={{ fontSize: '15px', color: c.text, marginBottom: '4px', fontWeight: '500' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '13px', color: c.textSubtle, lineHeight: 1.5 }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bottom tagline */}
            <div style={{
              textAlign: 'center',
              marginTop: '40px',
              paddingTop: '32px',
              borderTop: `1px solid ${c.border}`,
            }}>
              <div style={{ fontSize: '14px', color: c.textMuted, marginBottom: '8px' }}>
                Designet for den norske hverdagen
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '24px',
                flexWrap: 'wrap',
              }}>
                {['iOS', 'Android', 'Web'].map((platform, i) => (
                  <div key={i} style={{
                    fontSize: '11px',
                    color: c.textSubtle,
                    letterSpacing: '0.1em',
                    padding: '6px 12px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '4px',
                    border: `1px solid ${c.border}`,
                  }}>
                    {platform}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
      
      {/* App Gallery - Screenshots in phone mockups */}
      <FadeIn delay={0.25}>
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            fontSize: '12px', 
            color: c.textSubtle, 
            marginBottom: '24px', 
            letterSpacing: '0.15em',
            textAlign: 'center',
          }}>
            FRA APPEN
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'clamp(16px, 3vw, 32px)',
            maxWidth: '800px',
            margin: '0 auto',
          }} className="gallery-grid">
            {[
              { src: '/app_screenshot_1.png', label: 'Hjem' },
              { src: '/app_screenshot_4.png', label: 'Sjåfør' },
              { src: '/app_screenshot_3.png', label: 'Sporing' },
            ].map((screen, i) => (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
              }}>
                {/* Phone mockup */}
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '220px',
                }}>
                  {/* Shadow */}
                  <div style={{
                    position: 'absolute',
                    bottom: '-10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '70%',
                    height: '20px',
                    background: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)',
                    filter: 'blur(6px)',
                  }} />
                  
                  {/* Phone frame */}
                  <div style={{
                    background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 100%)',
                    borderRadius: '24px',
                    padding: '5px',
                    boxShadow: `
                      0 0 0 1px rgba(255,255,255,0.08),
                      0 15px 30px -8px rgba(0,0,0,0.4)
                    `,
                    position: 'relative',
                  }}>
                    {/* Dynamic Island */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60px',
                      height: '16px',
                      background: '#000',
                      borderRadius: '10px',
                      zIndex: 10,
                    }} />
                    
                    {/* Screen */}
                    <div style={{
                      borderRadius: '20px',
                      overflow: 'hidden',
                      background: '#000',
                    }}>
                      <img 
                        src={screen.src} 
                        alt={screen.label}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                      />
                    </div>
                    
                    {/* Home indicator */}
                    <div style={{
                      position: 'absolute',
                      bottom: '6px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60px',
                      height: '3px',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '2px',
                    }} />
                  </div>
                </div>
                
                {/* Label */}
                <div style={{ 
                  fontSize: '11px', 
                  color: c.textSubtle,
                  letterSpacing: '0.05em',
                }}>{screen.label}</div>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>
      
      {/* QR Code - Preview app on mobile */}
      <FadeIn delay={0.3}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '48px',
        }}>
          <div style={{
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: '16px',
            padding: 'clamp(20px, 4vw, 32px)',
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(20px, 4vw, 32px)',
            maxWidth: '480px',
          }} className="qr-container">
            {/* QR Code with black background */}
            <div style={{
              background: '#000',
              borderRadius: '12px',
              padding: '12px',
              flexShrink: 0,
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <img 
                src="/qr_code.png" 
                alt="QR-kode for app preview"
                style={{
                  width: 'clamp(80px, 15vw, 120px)',
                  height: 'clamp(80px, 15vw, 120px)',
                  display: 'block',
                }}
              />
            </div>
            
            {/* Text */}
            <div>
              <div style={{ 
                fontSize: '10px', 
                color: c.textSubtle, 
                letterSpacing: '0.15em',
                marginBottom: '8px',
              }}>
                PRØV SELV
              </div>
              <div style={{ 
                fontSize: 'clamp(14px, 2vw, 16px)', 
                color: c.text, 
                marginBottom: '6px',
                fontWeight: '500',
              }}>
                Preview appen på mobil
              </div>
              <div style={{ 
                fontSize: 'clamp(12px, 1.5vw, 13px)', 
                color: c.textSubtle,
                lineHeight: 1.5,
              }}>
                Skann QR-koden med telefonen for å teste appen direkte
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
      
      {/* Key differentiators */}
      <FadeIn delay={0.3}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="diff-grid">
          <Card>
            <div style={{ fontSize: '14px', color: c.text, marginBottom: '16px' }}>Hva plattformen gjør</div>
            <CheckItem>Formidler kontakt kunde ↔ sjåfør</CheckItem>
            <CheckItem>Beregner fastpris på sekunder</CheckItem>
            <CheckItem>Håndterer betaling trygt</CheckItem>
            <CheckItem>Sanntidssporing av leveranse</CheckItem>
            <CheckItem>Rating og tips (100% til sjåfør)</CheckItem>
          </Card>
          <Card>
            <div style={{ fontSize: '14px', color: c.text, marginBottom: '16px' }}>Hva plattformen ikke gjør</div>
            <div style={{ opacity: 0.5 }}>
              <CheckItem>Er ikke transportør</CheckItem>
              <CheckItem>Ansetter ikke sjåfører</CheckItem>
              <CheckItem>Eier ikke kjøretøy</CheckItem>
              <CheckItem>Tar ikke ansvar for gods</CheckItem>
            </div>
            <Text style={{ fontSize: '12px', marginTop: '12px', marginBottom: 0, fontStyle: 'italic' }}>
              Ren formidlingstjeneste – viktig juridisk avgrensning.
            </Text>
          </Card>
        </div>
      </FadeIn>
      
      <style>{`
        @media (max-width: 900px) {
          .video-showcase-grid { 
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .video-showcase-grid > div:first-child,
          .video-showcase-grid > div:last-child {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }
          .video-showcase-grid > div:nth-child(2) {
            order: -1;
            margin: 0 auto;
          }
          .feature-item-left {
            flex-direction: row !important;
            text-align: left !important;
          }
          .flow-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .flow-grid { 
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
          }
          .diff-grid { grid-template-columns: 1fr !important; }
          .video-showcase-grid > div:first-child,
          .video-showcase-grid > div:last-child {
            grid-template-columns: 1fr !important;
          }
          .gallery-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 12px !important;
          }
          .qr-container {
            flex-direction: column !important;
            text-align: center !important;
          }
        }
        @media (max-width: 400px) {
          .flow-grid { 
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STRATEGY - RECOMMENDED GO-TO-MARKET
// ═══════════════════════════════════════════════════════════════════════════

function StrategySection() {
  return (
    <Section id="strategy">
      <FadeIn>
        <Label>Anbefalt strategi</Label>
        <Title>Venteliste-modellen</Title>
        <Text style={{ maxWidth: '650px', marginBottom: '40px' }}>
          For en markedsplass er det kritisk å ha <strong style={{ color: c.text }}>supply før demand</strong>. 
          Vi anbefaler en strukturert utrulling som sikrer at du har sjåfører på plass før du åpner for kunder. 
          Dette er best practice for tosidig marked.
        </Text>
      </FadeIn>
      
      {/* Strategy steps */}
      <FadeIn delay={0.1}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '16px',
          marginBottom: '40px',
        }} className="strategy-grid">
          {[
            { 
              num: '1', 
              title: 'Rekruttér sjåfører', 
              desc: 'Målrettede annonser. Sjåfører registrerer seg på venteliste og blir godkjent.',
              icon: Users,
            },
            { 
              num: '2', 
              title: 'Bygg dekningsgrad', 
              desc: 'Sikre tilstrekkelig kapasitet i utvalgte postnummer før lansering.',
              icon: MapPin,
            },
            { 
              num: '3', 
              title: 'Lanser strategisk', 
              desc: 'Åpne område for område. Skalér kontrollert med fokus på kvalitet.',
              icon: Rocket,
            },
            { 
              num: '4', 
              title: 'Mål og iterer', 
              desc: 'Ukentlig analyse av metrics. Juster og optimaliser basert på data.',
              icon: BarChart3,
            },
          ].map((step, i) => (
            <Card key={i} style={{ padding: '24px', textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
                border: `1px solid ${c.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <step.icon size={20} color={c.textMuted} />
              </div>
              <div style={{ 
                fontSize: '10px', 
                color: c.textSubtle, 
                marginBottom: '8px',
                letterSpacing: '0.1em',
              }}>STEG {step.num}</div>
              <div style={{ fontSize: '15px', color: c.text, marginBottom: '8px', fontWeight: '500' }}>{step.title}</div>
              <Text style={{ fontSize: '12px', marginBottom: 0 }}>{step.desc}</Text>
            </Card>
          ))}
        </div>
      </FadeIn>
      
      {/* Why this works */}
      <FadeIn delay={0.2}>
        <Card highlight>
          <div style={{ fontSize: '14px', color: c.text, marginBottom: '16px', fontWeight: '500' }}>
            Hvorfor denne strategien fungerer
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {[
              { title: 'Kontrollert vekst', desc: 'Du unngår å brenne penger på kunder du ikke kan betjene' },
              { title: 'Bedre opplevelse', desc: 'Første kunder får rask service → positive anmeldelser' },
              { title: 'Datadrevet', desc: 'Du lærer hva som fungerer før du skalerer' },
              { title: 'Lavere risiko', desc: 'Enkelt å justere kursen underveis' },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ fontSize: '13px', color: c.text, marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '12px', color: c.textSubtle }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </Card>
      </FadeIn>
      
      <style>{`
        @media (max-width: 900px) {
          .strategy-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 500px) {
          .strategy-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASES - PILOT & PRODUCTION (REVISED)
// ═══════════════════════════════════════════════════════════════════════════

function PhasesSection() {
  return (
    <Section id="phases" alt>
      <FadeIn>
        <Label>Leveranse</Label>
        <Title>Tre faser: Pilot → GTM → Produksjon</Title>
        <Text style={{ maxWidth: '700px', marginBottom: '40px' }}>
          Vi anbefaler en strukturert tilnærming med tre faser. <strong style={{ color: c.text }}>Piloten</strong> validerer 
          produktet teknisk. <strong style={{ color: c.text }}>GTM-fasen</strong> tester markedet og bygger etterspørsel.
          {' '}<strong style={{ color: c.text }}>Produksjonsfasen</strong> handler om sikkerhet og skalering før offentlig lansering.
        </Text>
      </FadeIn>
      
      {/* PHASE 1: PILOT */}
      <FadeIn delay={0.1}>
        <Card highlight style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            <div>
              <div style={{ display: 'inline-block', fontSize: '9px', color: c.bg, background: c.text, padding: '4px 10px', borderRadius: '4px', marginBottom: '8px', letterSpacing: '0.1em' }}>ANBEFALT START</div>
              <div style={{ fontSize: '22px', fontWeight: '300', color: c.text }}>Fase 1: Pilot</div>
              <div style={{ fontSize: '12px', color: c.textSubtle, marginTop: '4px' }}>Produktvalidering</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: c.textSubtle }}>VARIGHET</div>
              <div style={{ fontSize: '18px', color: c.text }}>2-4 uker</div>
            </div>
          </div>
          
          <Text style={{ marginBottom: '20px' }}>
            En komplett, fungerende app du kan teste med ekte brukere. Inkluderer betalingsløsning og native app 
            for TestFlight/intern testing. Validerer konseptet før du investerer videre.
          </Text>
          
          {/* Customer collaboration note */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${c.border}`,
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}>
            <Users size={16} color={c.textMuted} style={{ marginTop: '2px', flexShrink: 0 }} />
            <Text style={{ fontSize: '13px', marginBottom: 0, color: c.textMuted }}>
              <strong style={{ color: c.text }}>Skreddersydd sammen med deg.</strong> Endelig funksjonalitet og prioriteringer 
              fastsettes i oppstartsmøte. Listen under er vårt forslag basert på samtalen vår.
            </Text>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }} className="pilot-features">
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>KUNDE-APP</div>
              <CheckItem>Registrering & innlogging</CheckItem>
              <CheckItem>Bestill transport (A → B)</CheckItem>
              <CheckItem>Velg kjøretøytype</CheckItem>
              <CheckItem>Se fastpris umiddelbart</CheckItem>
              <CheckItem>Sanntidssporing av sjåfør</CheckItem>
              <CheckItem>Chat med sjåfør</CheckItem>
              <CheckItem>Rating og tips</CheckItem>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>SJÅFØR-APP</div>
              <CheckItem>Registrering & onboarding</CheckItem>
              <CheckItem>Se tilgjengelige oppdrag</CheckItem>
              <CheckItem>Aksepter/avslå jobber</CheckItem>
              <CheckItem>Navigasjon til henting/levering</CheckItem>
              <CheckItem>Oppdater status underveis</CheckItem>
              <CheckItem>Se inntektsoversikt</CheckItem>
              <CheckItem>Chat med kunde</CheckItem>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>BETALING & ADMIN</div>
              <CheckItem>Stripe/Vipps betaling</CheckItem>
              <CheckItem>Utbetaling til sjåfører</CheckItem>
              <CheckItem>Tips (100% til sjåfør)</CheckItem>
              <CheckItem>Admin-dashboard (web)</CheckItem>
              <CheckItem>Godkjenn sjåførsøknader</CheckItem>
              <CheckItem>Statistikk & oversikt</CheckItem>
            </div>
          </div>
          
          <Divider />
          <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>IKKE INKLUDERT I PILOT</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', opacity: 0.6 }}>
            {['App Store publisering', 'Play Store publisering', 'Sikkerhetsaudit', 'Lastbalansering', 'GDPR-dokumentasjon'].map((item, i) => (
              <span key={i} style={{ fontSize: '12px', color: c.textSubtle }}>• {item}</span>
            ))}
          </div>
        </Card>
      </FadeIn>
      
      {/* GO/NO-GO #1 */}
      <FadeIn delay={0.15}>
        <Card style={{ marginBottom: '20px', background: c.bgAlt }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <Target size={18} color={c.textMuted} />
            <span style={{ fontSize: '14px', color: c.text }}>Go / No-go beslutning #1</span>
          </div>
          <Text style={{ fontSize: '13px', marginBottom: '16px' }}>
            Etter piloten tar vi en strukturert beslutning sammen. Pilotdata gir grunnlag for å vurdere veien videre.
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
            {[
              { label: 'GO', desc: 'Teknisk OK → GTM' },
              { label: 'PIVOT', desc: 'Justerer retning' },
              { label: 'STOP', desc: 'Avslutter prosjektet' },
            ].map((item, i) => (
              <div key={i} style={{ background: c.bgCard, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: c.text, marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '11px', color: c.textSubtle }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '11px', color: c.textSubtle, fontStyle: 'italic', paddingTop: '12px', borderTop: `1px solid ${c.border}` }}>
            Piloten validerer teknisk og operasjonell gjennomførbarhet. Markedsrespons påvirkes av eksterne faktorer og kan ikke garanteres.
          </div>
        </Card>
      </FadeIn>
      
      {/* PHASE 1.5: GTM / KOMMERSIELL PILOT */}
      <FadeIn delay={0.18}>
        <Card style={{ marginBottom: '20px', border: `1px solid rgba(34, 197, 94, 0.3)` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            <div>
              <div style={{ 
                display: 'inline-block', 
                fontSize: '9px', 
                color: '#22c55e', 
                background: 'rgba(34, 197, 94, 0.1)', 
                border: '1px solid rgba(34, 197, 94, 0.3)',
                padding: '4px 10px', 
                borderRadius: '4px', 
                marginBottom: '8px', 
                letterSpacing: '0.1em' 
              }}>ANBEFALT</div>
              <div style={{ fontSize: '22px', fontWeight: '300', color: c.text }}>Fase 1.5: Kommersiell pilot</div>
              <div style={{ fontSize: '12px', color: c.textSubtle, marginTop: '4px' }}>Go-To-Market & Markedsvalidering</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: c.textSubtle }}>VARIGHET</div>
              <div style={{ fontSize: '18px', color: c.text }}>4-8 uker</div>
            </div>
          </div>
          
          <Text style={{ marginBottom: '20px' }}>
            Denne fasen svarer på det viktigste spørsmålet: <strong style={{ color: c.text }}>"Kan dette bli en business?"</strong> Vi 
            aktiverer markedet, skaper etterspørsel, og måler reell bruk. Dette gir et solid beslutningsgrunnlag før 
            større investeringer i produksjon.
          </Text>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>SUPPLY-SIDE</div>
              <CheckItem>Sjåførrekruttering</CheckItem>
              <CheckItem>Onboarding & opplæring</CheckItem>
              <CheckItem>Incentivprogram</CheckItem>
              <CheckItem>Kapasitetsplanlegging</CheckItem>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>DEMAND-SIDE</div>
              <CheckItem>Kundeakkvisisjon</CheckItem>
              <CheckItem>Kampanjer & annonsering</CheckItem>
              <CheckItem>PR & synlighet</CheckItem>
              <CheckItem>Referralprogram</CheckItem>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>ANALYSE & OPERASJON</div>
              <CheckItem>KPI-dashboard</CheckItem>
              <CheckItem>Konverteringsanalyse</CheckItem>
              <CheckItem>Operasjonell support</CheckItem>
              <CheckItem>Iterasjon på feedback</CheckItem>
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(34, 197, 94, 0.05)', 
            border: '1px solid rgba(34, 197, 94, 0.15)',
            borderRadius: '8px', 
            padding: '14px 16px',
            marginBottom: '16px',
          }}>
            <Text style={{ fontSize: '12px', marginBottom: 0, color: c.textMuted }}>
              <strong style={{ color: c.text }}>Valgfri, men anbefalt.</strong> For kunder som ønsker bistand med kommersialisering 
              og pilot-aktivering. Gir et bedre beslutningsgrunnlag for videre investering i produksjon.
            </Text>
          </div>
          
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ fontSize: '12px', color: c.textSubtle }}>
              Uten GTM-fase risikerer man at pilot "ser bra ut" teknisk, men mangler reell etterspørsel.
            </div>
          </div>
        </Card>
      </FadeIn>
      
      {/* GO/NO-GO #2 */}
      <FadeIn delay={0.22}>
        <Card style={{ marginBottom: '20px', background: c.bgAlt }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <Target size={18} color={c.textMuted} />
            <span style={{ fontSize: '14px', color: c.text }}>Go / No-go beslutning #2</span>
          </div>
          <Text style={{ fontSize: '13px', marginBottom: '16px' }}>
            Etter GTM-fasen har vi data på faktisk markedsrespons. Dette gir et solid grunnlag for produksjonsbeslutningen.
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            {[
              { label: 'GO', desc: 'Traction OK → Produksjon' },
              { label: 'PIVOT', desc: 'Justerer strategi' },
              { label: 'STOP', desc: 'Avslutter før store kostnader' },
            ].map((item, i) => (
              <div key={i} style={{ background: c.bgCard, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: c.text, marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '11px', color: c.textSubtle }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </Card>
      </FadeIn>
      
      {/* PHASE 2: PRODUCTION */}
      <FadeIn delay={0.25}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '10px', color: c.textSubtle, letterSpacing: '0.1em', marginBottom: '4px' }}>ETTER GTM</div>
              <div style={{ fontSize: '22px', fontWeight: '300', color: c.text }}>Fase 2: Produksjon</div>
              <div style={{ fontSize: '12px', color: c.textSubtle, marginTop: '4px' }}>Sikkerhet & Skalering</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: c.textSubtle }}>VARIGHET</div>
              <div style={{ fontSize: '18px', color: c.text }}>Avtales</div>
            </div>
          </div>
          
          <Text style={{ marginBottom: '20px' }}>
            Hardening og klargjøring for offentlig lansering. Fokus på sikkerhet, ytelse, skalering og compliance. 
            Alt som trengs for å gå live i App Store og Play Store.
          </Text>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>SIKKERHET</div>
              <CheckItem>Sikkerhetsaudit</CheckItem>
              <CheckItem>Penetrasjonstesting</CheckItem>
              <CheckItem>GDPR-dokumentasjon</CheckItem>
              <CheckItem>Datakryptering</CheckItem>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>SKALERING</div>
              <CheckItem>Lastbalansering</CheckItem>
              <CheckItem>CDN-oppsett</CheckItem>
              <CheckItem>Database-optimalisering</CheckItem>
              <CheckItem>Auto-skalering</CheckItem>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '8px' }}>LANSERING</div>
              <CheckItem>App Store publisering</CheckItem>
              <CheckItem>Play Store publisering</CheckItem>
              <CheckItem>Monitoring & alerting</CheckItem>
              <CheckItem>Support-system</CheckItem>
            </div>
          </div>
        </Card>
      </FadeIn>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TIMELINE
// ═══════════════════════════════════════════════════════════════════════════

function TimelineSection() {
  return (
    <Section id="timeline">
      <FadeIn>
        <Label>Tidsplan</Label>
        <Title size="md">Hva skjer når?</Title>
      </FadeIn>
      
      <FadeIn delay={0.1}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            {[
              { phase: 'PILOT', time: 'Uke 1-4', items: ['Komplett app', 'Betaling integrert', 'TestFlight klar'] },
              { phase: 'GTM', time: 'Uke 5-12', items: ['Sjåførrekruttering', 'Kundeakkvisisjon', 'Markedstest'] },
              { phase: 'PRODUKSJON', time: 'Etter GTM', items: ['Sikkerhet', 'Skalering', 'App Store'] },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ fontSize: '10px', color: c.textSubtle, letterSpacing: '0.1em', marginBottom: '4px' }}>{item.phase}</div>
                <div style={{ fontSize: '16px', color: c.text, marginBottom: '12px' }}>{item.time}</div>
                {item.items.map((step, j) => (
                  <div key={j} style={{ fontSize: '12px', color: c.textMuted, marginBottom: '4px' }}>• {step}</div>
                ))}
              </div>
            ))}
          </div>
          
          <div style={{ background: c.bgAlt, borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
              <div style={{ flex: 2, background: c.text }} />
              <div style={{ flex: 4, background: 'rgba(34, 197, 94, 0.5)' }} />
              <div style={{ flex: 3, background: 'rgba(255,255,255,0.15)' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: c.textSubtle }}>
              <span>Start</span>
              <span>Pilot klar</span>
              <span>GTM ferdig</span>
              <span>Lansering</span>
            </div>
          </div>
        </Card>
      </FadeIn>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// INVESTMENT
// ═══════════════════════════════════════════════════════════════════════════

function InvestmentSection() {
  return (
    <Section id="investment" alt>
      <FadeIn>
        <Label>Investering</Label>
        <Title>Pris og betaling</Title>
        <Text style={{ maxWidth: '650px', marginBottom: '40px' }}>
          Vi anbefaler en strukturert investering i tre faser. Start med piloten, vurder GTM for markedsvalidering, 
          og gå videre til produksjon når dere har traction.
        </Text>
      </FadeIn>
      
      {/* THREE PHASE PRICING */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {/* PILOT */}
        <FadeIn delay={0.1}>
          <Card highlight>
            <div style={{ display: 'inline-block', fontSize: '9px', color: c.bg, background: c.text, padding: '4px 10px', borderRadius: '4px', marginBottom: '12px', letterSpacing: '0.1em' }}>
              SELGES NÅ
            </div>
            <div style={{ fontSize: '10px', color: c.textSubtle, letterSpacing: '0.1em', marginBottom: '4px' }}>FASE 1: PILOT</div>
            <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '16px' }}>Produktvalidering · 2-4 uker</div>
            
            <div style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: '200', color: c.text, marginBottom: '4px' }}>300.000 kr</div>
            <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '16px' }}>Fastpris</div>
            
            <Text style={{ fontSize: '13px', marginBottom: '20px' }}>
              Komplett, fungerende app med betaling. Validerer konseptet teknisk før videre investering.
            </Text>
            
            <div style={{ background: c.bgAlt, borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '10px' }}>BETALINGSPLAN</div>
              {[
                { label: '40% ved signering', amount: '120.000 kr' },
                { label: '30% ved demo', amount: '90.000 kr' },
                { label: '30% ved leveranse', amount: '90.000 kr' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                  <span style={{ color: c.textMuted }}>{item.label}</span>
                  <span style={{ color: c.text }}>{item.amount}</span>
                </div>
              ))}
            </div>
            
            <Divider />
            <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '10px' }}>INKLUDERT</div>
            <div style={{ fontSize: '12px' }}>
              <CheckItem>Native app (iOS + Android)</CheckItem>
              <CheckItem>Betalingsløsning (Stripe/Vipps)</CheckItem>
              <CheckItem>Admin-dashboard</CheckItem>
              <CheckItem>TestFlight distribusjon</CheckItem>
              <CheckItem>Kildekode overlevert</CheckItem>
            </div>
          </Card>
        </FadeIn>
        
        {/* GTM / KOMMERSIELL PILOT */}
        <FadeIn delay={0.15}>
          <Card style={{ border: `1px solid rgba(34, 197, 94, 0.3)` }}>
            <div style={{ 
              display: 'inline-block', 
              fontSize: '9px', 
              color: '#22c55e', 
              background: 'rgba(34, 197, 94, 0.1)', 
              border: '1px solid rgba(34, 197, 94, 0.3)',
              padding: '4px 10px', 
              borderRadius: '4px', 
              marginBottom: '12px', 
              letterSpacing: '0.1em' 
            }}>
              ANBEFALT
            </div>
            <div style={{ fontSize: '10px', color: c.textSubtle, letterSpacing: '0.1em', marginBottom: '4px' }}>FASE 1.5: KOMMERSIELL PILOT</div>
            <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '16px' }}>Go-To-Market · 4-8 uker</div>
            
            <div style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: '200', color: c.text, marginBottom: '4px' }}>200.000 kr</div>
            <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '16px' }}>Estimat</div>
            
            <Text style={{ fontSize: '13px', marginBottom: '20px' }}>
              Markedsvalidering og aktivering. Svarer på: <strong style={{ color: c.text }}>"Kan dette bli en business?"</strong>
            </Text>
            
            <div style={{ background: 'rgba(34, 197, 94, 0.05)', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '10px' }}>HVORFOR DENNE FASEN?</div>
              <Text style={{ fontSize: '12px', marginBottom: 0, color: c.textMuted }}>
                Uten GTM-fase risikerer man at piloten "ser bra ut" teknisk, men mangler reell markedsrespons. 
                GTM gir et solid beslutningsgrunnlag.
              </Text>
            </div>
            
            <Divider />
            <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '10px' }}>INKLUDERT</div>
            <div style={{ fontSize: '12px' }}>
              <CheckItem>Sjåførrekruttering & onboarding</CheckItem>
              <CheckItem>Kundeakkvisisjon</CheckItem>
              <CheckItem>Kampanjer & annonsering</CheckItem>
              <CheckItem>KPI-dashboard & analyse</CheckItem>
              <CheckItem>Operasjonell support</CheckItem>
            </div>
          </Card>
        </FadeIn>
        
        {/* PRODUCTION */}
        <FadeIn delay={0.2}>
          <Card>
            <div style={{ fontSize: '10px', color: c.textSubtle, letterSpacing: '0.1em', marginBottom: '4px' }}>FASE 2: PRODUKSJON</div>
            <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '16px' }}>Sikkerhet & Lansering</div>
            
            <div style={{ 
              fontSize: '14px', 
              color: c.textSubtle, 
              textDecoration: 'line-through',
              marginBottom: '4px',
              opacity: 0.6,
            }}>
              1,5 – 2,0 MNOK
            </div>
            
            <div style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: '200', color: c.text, marginBottom: '4px' }}>950.000 kr</div>
            <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '16px' }}>Fastpris</div>
            
            <Text style={{ fontSize: '13px', marginBottom: '20px' }}>
              Hardening, sikkerhet og skalering for offentlig lansering i App Store og Play Store.
            </Text>
            
            <div style={{ background: c.bgAlt, borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '10px' }}>BETALINGSPLAN</div>
              {[
                { label: '30% ved signering', amount: '285.000 kr' },
                { label: '30% ved beta', amount: '285.000 kr' },
                { label: '30% ved App Store', amount: '285.000 kr' },
                { label: '10% ved godkjenning', amount: '95.000 kr' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                  <span style={{ color: c.textMuted }}>{item.label}</span>
                  <span style={{ color: c.text }}>{item.amount}</span>
                </div>
              ))}
            </div>
            
            <Divider />
            <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '10px' }}>INKLUDERT</div>
            <div style={{ fontSize: '12px' }}>
              <CheckItem>Sikkerhetsaudit</CheckItem>
              <CheckItem>Skalering & lastbalansering</CheckItem>
              <CheckItem>App Store publisering</CheckItem>
              <CheckItem>Monitoring & support</CheckItem>
              <CheckItem>Kildekode overlevert</CheckItem>
            </div>
          </Card>
        </FadeIn>
      </div>
      
      {/* ONGOING COSTS - Estimates */}
      <FadeIn delay={0.2}>
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', color: c.text, marginBottom: '6px' }}>Estimert løpende kostnadsnivå</div>
          <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '8px' }}>
            Grove estimater basert på forventet bruk i tidlig fase
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: c.accent, 
            padding: '10px 14px', 
            background: 'rgba(0, 122, 255, 0.08)', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: `1px solid rgba(0, 122, 255, 0.15)`,
          }}>
            💡 Hosting hos oss er <strong>valgfritt</strong>. Dere kan velge å drifte løsningen selv eller hos annen leverandør etter overlevering av kildekode.
          </div>
          
          <div style={{ fontSize: '11px', color: c.textSubtle, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ved hosting hos Autonomous</div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '200', color: c.text, marginBottom: '4px' }}>~10–15.000 kr<span style={{ fontSize: '12px', color: c.textSubtle }}>/mnd</span></div>
              <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '4px' }}>DRIFT & HOSTING</div>
              <Text style={{ fontSize: '11px', marginBottom: 0 }}>Cloud-infrastruktur, backup, sikkerhet, 99.9% uptime</Text>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '200', color: c.text, marginBottom: '4px' }}>~8–12.000 kr<span style={{ fontSize: '12px', color: c.textSubtle }}>/mnd</span></div>
              <div style={{ fontSize: '12px', color: c.textSubtle, marginBottom: '4px' }}>SUPPORT & VEDLIKEHOLD</div>
              <Text style={{ fontSize: '11px', marginBottom: 0 }}>Bug fixes, småjusteringer, teknisk rådgivning</Text>
            </div>
          </div>
          <div style={{ marginTop: '16px', padding: '12px 14px', background: c.bgAlt, borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: c.text }}>Totalt estimert*</span>
              <span style={{ fontSize: '15px', color: c.text, fontWeight: '500' }}>~18.000 – 25.000 kr/mnd</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: `1px solid ${c.border}` }}>
              <span style={{ fontSize: '12px', color: c.textSubtle }}>Intro-pris første 6 mnd</span>
              <span style={{ fontSize: '13px', color: c.text }}>~13.000 kr/mnd</span>
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', color: c.textSubtle, fontStyle: 'italic' }}>
            * Estimatene er veiledende og vil variere basert på faktisk trafikk, bruksmønster og eventuelle tredjepartstjenester. 
            Endelig pris avtales i dialog.
          </div>
        </Card>
      </FadeIn>
      
      {/* GUARANTEES */}
      <FadeIn delay={0.3}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          {[
            { icon: Shield, label: 'Fastpris-garanti', desc: 'Ingen merkostnader' },
            { icon: RefreshCw, label: 'Fleksibel utvikling', desc: 'Justeringer inkludert' },
            { icon: Code, label: '100% eierskap', desc: 'Din kode, dine data' },
            { icon: Lock, label: 'Ingen lock-in', desc: '3 mnd oppsigelse' },
          ].map((item, i) => (
            <Card key={i} style={{ textAlign: 'center', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <item.icon size={20} color={c.textMuted} style={{ marginBottom: '10px' }} />
              <div style={{ fontSize: '13px', color: c.text, marginBottom: '4px' }}>{item.label}</div>
              <div style={{ fontSize: '11px', color: c.textSubtle }}>{item.desc}</div>
            </Card>
          ))}
        </div>
      </FadeIn>
      
      <style>{`
        @media (max-width: 768px) {
          .gtm-grid { grid-template-columns: 1fr !important; }
          .gtm-includes { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FAQ
// ═══════════════════════════════════════════════════════════════════════════

function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: 'Hva hvis markedet ikke responderer som forventet?',
      a: 'Da har du brukt 300k på å få et klart svar – før du investerte millioner. Du sitter igjen med verdifull innsikt, konkrete data og en fungerende prototype du eier 100%. Mange av de beste pivotene starter med akkurat denne typen læring.'
    },
    {
      q: 'Hva skiller pilot fra produksjon?',
      a: 'Piloten er en komplett, fungerende app med betaling – klar for testing via TestFlight med ekte brukere. Produksjonsfasen handler om sikkerhet, skalering og klargjøring for offentlig lansering i App Store og Play Store.'
    },
    {
      q: 'Kan jeg gjøre endringer underveis?',
      a: 'Ja. Endringer og justeringer innenfor avtalt fase og mål er inkludert i fastprisen. Nye funksjonsområder og større utvidelser vurderes og prioriteres sammen, slik at vi alltid jobber mot det som gir mest verdi.'
    },
    {
      q: 'Hvorfor er dere så mye raskere enn tradisjonelle byråer?',
      a: 'Vi bruker AI-agenter til å bygge kode i sanntid, mens erfarne mennesker validerer kvalitet og sikkerhet. Der andre planlegger sprinter på uker, itererer vi på minutter. Resultatet er kortere tid til markedet uten å gå på kompromiss med kvalitet.'
    },
    {
      q: 'Hva skjer med koden hvis vi velger å ikke gå videre?',
      a: 'Du eier 100% av alt vi lager – kode, design, dokumentasjon. Du står fritt til å ta det videre med et annet team, bygge internt, eller la det ligge. Ingen lock-in, ingen bindingstid.'
    },
    {
      q: 'Er produktnavnet endelig bestemt?',
      a: 'Nei, navnet brukt i dette tilbudet er kun et arbeidsnavn. Vi kan endre navn, logo og visuell profil når som helst uten at det påvirker scope eller pris.'
    },
    {
      q: 'Hva dekker de løpende kostnadene?',
      a: 'Estimerte månedskostnader (18-25k) inkluderer cloud-hosting med 99.9% uptime, backup, sikkerhet, og support/vedlikehold. De første 6 månedene tilbyr vi intro-pris på ca. 13k/mnd. Endelig kostnad avhenger av trafikk og bruksmønster.'
    },
  ];
  
  return (
    <Section id="faq">
      <FadeIn>
        <Label>Vanlige spørsmål</Label>
        <Title size="md">Spørsmål & svar</Title>
      </FadeIn>
      
      <div style={{ marginTop: '24px' }}>
        {faqs.map((faq, i) => (
          <FadeIn key={i} delay={0.03 * i}>
            <div style={{ borderBottom: `1px solid ${c.border}`, paddingBottom: '16px', marginBottom: '16px' }}>
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '14px', color: c.text, paddingRight: '16px' }}>{faq.q}</span>
                <ChevronRight size={16} color={c.textSubtle} style={{
                  transform: openIndex === i ? 'rotate(90deg)' : 'rotate(0)',
                  transition: 'transform 0.2s', flexShrink: 0,
                }} />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Text style={{ marginTop: '12px', marginBottom: 0, fontSize: '13px' }}>{faq.a}</Text>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTACT
// ═══════════════════════════════════════════════════════════════════════════

function ContactSection() {
  return (
    <Section id="contact" alt>
      <FadeIn>
        <Label>Neste steg</Label>
        <Title>Klar for å starte?</Title>
      </FadeIn>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '40px' }} className="steps-grid">
        {[
          { num: '1', title: 'Møte', desc: 'Gå gjennom tilbudet', time: '30 min' },
          { num: '2', title: 'Avtale', desc: 'Enige om scope', time: '1-2 dager' },
          { num: '3', title: 'Pilot', desc: 'Komplett app klar', time: '2-4 uker' },
          { num: '4', title: 'Beslutning', desc: 'Go / Pivot / Stop', time: 'Uke 3-4' },
        ].map((step, i) => (
          <FadeIn key={i} delay={0.05 * i}>
            <Card style={{ padding: '16px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                border: `1px solid ${c.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', color: c.text, marginBottom: '10px',
              }}>{step.num}</div>
              <div style={{ fontSize: '14px', color: c.text, marginBottom: '4px' }}>{step.title}</div>
              <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '6px' }}>{step.desc}</div>
              <div style={{ fontSize: '10px', color: c.textSubtle }}>{step.time}</div>
            </Card>
          </FadeIn>
        ))}
      </div>
      
      <FadeIn delay={0.3}>
        <Card style={{ textAlign: 'center', padding: 'clamp(32px, 5vw, 56px)' }}>
          <div style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: '300', color: c.text, marginBottom: '12px' }}>
            Interessert?
          </div>
          <Text style={{ marginBottom: '24px' }}>
            Ta kontakt for en uforpliktende prat. Vi svarer innen 24 timer.
          </Text>
          
          <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px' }}>
            <a href="mailto:martin@autonomous.no" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: c.text, textDecoration: 'none', fontSize: '13px' }}>
              <Mail size={14} /> martin@autonomous.no
            </a>
            <a href="tel:+4740400758" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: c.text, textDecoration: 'none', fontSize: '13px' }}>
              <Phone size={14} /> +47 404 00 758
            </a>
          </div>
          
          <button
            onClick={() => window.location.href = 'mailto:martin@autonomous.no?subject=VareTaxi%20-%20Interesse'}
            style={{
              background: c.text, color: c.bg, border: 'none', borderRadius: '8px',
              padding: '14px 36px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '8px',
            }}
          >
            Start samtalen <ArrowRight size={14} />
          </button>
        </Card>
      </FadeIn>
      
      <FadeIn delay={0.4}>
        <div style={{ textAlign: 'center', marginTop: '48px', paddingTop: '32px', borderTop: `1px solid ${c.border}` }}>
          <Text style={{ marginBottom: '16px', fontSize: '13px' }}>Takk for at du tok deg tid til å lese tilbudet.</Text>
          
          {/* Partnership disclaimer */}
          <div style={{ 
            fontSize: '11px', 
            color: c.textSubtle, 
            fontStyle: 'italic',
            maxWidth: '500px',
            margin: '0 auto 24px',
            lineHeight: 1.6,
          }}>
            Autonomous bistår som teknologisk og strategisk partner. Endelige forretningsbeslutninger og markedsstrategi eies av kunden.
          </div>
          
          {/* Footer with company info */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '16px',
            marginTop: '24px',
          }}>
            {/* Logo */}
            <a href="https://autonomous.no" target="_blank" rel="noopener noreferrer" style={{ 
              fontSize: '28px', 
              fontWeight: '100', 
              color: c.text, 
              textDecoration: 'none',
              letterSpacing: '-0.02em',
            }}>a.</a>
            
            {/* Links and info */}
            <div style={{ 
              display: 'flex', 
              gap: '24px', 
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              <a 
                href="https://autonomous.no" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  fontSize: '12px', 
                  color: c.textMuted, 
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
              >
                autonomous.no
              </a>
              <span style={{ fontSize: '12px', color: c.textSubtle }}>·</span>
              <span style={{ fontSize: '12px', color: c.textSubtle }}>Org.nr 936 856 055</span>
            </div>
            
            {/* Copyright */}
            <div style={{ 
              fontSize: '11px', 
              color: c.textSubtle,
              marginTop: '8px',
            }}>
              © {new Date().getFullYear()} Autonomous AS
            </div>
          </div>
        </div>
      </FadeIn>
      
      <style>{`
        @media (max-width: 600px) {
          .steps-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LOADING SCREEN
// ═══════════════════════════════════════════════════════════════════════════

function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(onComplete, 400);
          return 100;
        }
        return prev + 2;
      });
    }, 30);
    return () => clearInterval(timer);
  }, [onComplete]);
  
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease }}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      {/* Logo - matching root page style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease }}
        style={{
          position: 'relative',
        }}
      >
        {/* Glow effect */}
        <div style={{
          position: 'absolute',
          inset: '-60px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }} />
        
        <motion.span
          animate={{ 
            y: [0, -6, 0],
          }}
          transition={{ 
            y: { 
              duration: 3, 
              repeat: Infinity, 
              ease: 'easeInOut',
              delay: 0.5,
            }
          }}
          style={{
            fontSize: 'clamp(100px, 30vw, 200px)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: '100',
            letterSpacing: '-0.06em',
            color: '#fff',
            lineHeight: 0.75,
            display: 'block',
            position: 'relative',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          a.
        </motion.span>
      </motion.div>
      
      {/* Company name - lowercase like root */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3, ease }}
        style={{
          fontSize: 'clamp(14px, 3vw, 18px)',
          fontWeight: '400',
          letterSpacing: '0.35em',
          color: 'rgba(255,255,255,0.5)',
          marginTop: 'clamp(24px, 5vh, 48px)',
        }}
      >
        autonomous
      </motion.p>
      
      {/* Progress bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.6 }}
        style={{
          width: 'clamp(180px, 35vw, 260px)',
          height: '1px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '1px',
          overflow: 'hidden',
          marginTop: 'clamp(40px, 8vh, 80px)',
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.1 }}
          style={{
            height: '100%',
            background: 'rgba(255,255,255,0.5)',
          }}
        />
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════

export default function ProposalPage() {
  const [isLoading, setIsLoading] = useState(true);
  
  // SEO: Set document title, meta tags, and Open Graph
  useEffect(() => {
    // Page-specific SEO data
    const seo = {
      title: 'Tilbud: VareTaxi | Autonomous AS',
      description: 'Prosjekttilbud fra Autonomous AS – Vi bygger din markedsplass for varetransport. Pilot på 2-4 uker, fastpris 300.000 kr. AI-drevet utvikling med full kildekode-eierskap.',
      url: 'https://autonomous.no/pedersen/tilbud',
      image: 'https://pixeltrack-crm.preview.emergentagent.com/og-proposal.png',
      siteName: 'Autonomous AS',
      locale: 'nb_NO',
      type: 'website',
    };
    
    // Update document title
    document.title = seo.title;
    
    // Helper to update or create meta tags
    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    
    // Primary Meta Tags
    setMeta('title', seo.title);
    setMeta('description', seo.description);
    setMeta('author', 'Autonomous AS');
    setMeta('robots', 'index, follow');
    setMeta('keywords', 'varetaxi, varetransport, on-demand levering, app utvikling, markedsplass, pilot, MVP, Autonomous, Bergen, Norge');
    
    // Open Graph / Facebook
    setMeta('og:type', seo.type, true);
    setMeta('og:url', seo.url, true);
    setMeta('og:title', seo.title, true);
    setMeta('og:description', seo.description, true);
    setMeta('og:image', seo.image, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:site_name', seo.siteName, true);
    setMeta('og:locale', seo.locale, true);
    
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', seo.url);
    setMeta('twitter:title', seo.title);
    setMeta('twitter:description', seo.description);
    setMeta('twitter:image', seo.image);
    
    // Additional SEO best practices
    setMeta('theme-color', '#000000');
    setMeta('apple-mobile-web-app-capable', 'yes');
    setMeta('apple-mobile-web-app-status-bar-style', 'black-translucent');
    setMeta('format-detection', 'telephone=no');
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seo.url);
    
    // JSON-LD Structured Data
    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: seo.title,
      description: seo.description,
      url: seo.url,
      publisher: {
        '@type': 'Organization',
        name: 'Autonomous AS',
        url: 'https://autonomous.no',
        logo: {
          '@type': 'ImageObject',
          url: 'https://autonomous.no/logo.png',
        },
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'NO',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+47-404-00-758',
          email: 'martin@autonomous.no',
          contactType: 'sales',
        },
      },
      mainEntity: {
        '@type': 'Offer',
        name: 'VareTaxi Pilot',
        description: 'Komplett app-pilot for on-demand varetransport. Native iOS/Android app, betalingsløsning, og admin-dashboard.',
        price: '300000',
        priceCurrency: 'NOK',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'Autonomous AS',
        },
      },
    });
    
    // Cleanup function
    return () => {
      // Reset to default title when leaving page
      document.title = 'Autonomous | Vi syssler med AI';
    };
  }, []);
  
  // Set body class for CSS override
  useEffect(() => {
    document.body.classList.add('proposal-body');
    document.documentElement.classList.add('proposal-html');
    return () => {
      document.body.classList.remove('proposal-body');
      document.documentElement.classList.remove('proposal-html');
    };
  }, []);

  return (
    <div 
      data-page="proposal"
      style={{
        background: c.bg, color: c.text,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        minHeight: '100vh', WebkitFontSmoothing: 'antialiased',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@200;300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::selection { background: rgba(255,255,255,0.2); }
        html, body { background: #000 !important; }
      `}</style>
      
      <AnimatePresence>
        {isLoading && <LoadingScreen onComplete={() => setIsLoading(false)} />}
      </AnimatePresence>
      
      {!isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Nav />
          <Hero />
          <PersonalGreeting />
          <ProblemSection />
          <SolutionSection />
          <StrategySection />
          <PhasesSection />
          <TimelineSection />
          <InvestmentSection />
          <AboutSection />
          <FAQSection />
          <ContactSection />
        </motion.div>
      )}
    </div>
  );
}
